import { useEffect, useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import NavBar from "./components/NavBar";
import MobileTabBar from "./components/MobileTabBar";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import { api, setTokenGetter } from "./api/client";
import { identifyUser, isMobile, readRef } from "./lib/analytics";
import type { Me } from "./types";

/** Registers Clerk's getToken with the plain-function API client so every REST
 *  request and the voice WebSocket attach a Bearer token. */
function ClerkTokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setTokenGetter(() => getToken());
  }, [getToken]);
  return null;
}

/** Renders the app shell and, on a new user's first sign-in, overlays the
 *  onboarding wizard. The overlay is opaque and only mounts after `/api/me`
 *  resolves with `onboarded === false`, so onboarded users never see a flash. */
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.getMe().then((m) => {
      setMe(m);
      // Stitch the anonymous landing-page history onto this account. Done the
      // moment /me resolves — it's the first point where the Clerk id, plan and
      // level are all known.
      identifyUser(m.clerk_id, {
        plan: m.plan,
        tutor_level: m.tutor_level,
        signup_date: m.created_at,
        ref: readRef(),
        is_mobile: isMobile(),
      });
    }).catch(() => { /* keep app usable if /me fails */ });
  }, []);

  const showWizard = me !== null && !me.onboarded && !done;

  return (
    <>
      {children}
      {showWizard && (
        <OnboardingWizard onComplete={() => setDone(true)} />
      )}
    </>
  );
}

/** Shape of the context passed to routed pages via <Outlet>. ConversationPage
 *  flips `setSessionActive` while a voice session is live so the mobile tab
 *  bar gets out of the way of the mic controls. */
export type AppOutletContext = { setSessionActive: (active: boolean) => void };

export function useAppOutletContext() {
  return useOutletContext<AppOutletContext>();
}

export default function App() {
  const [sessionActive, setSessionActive] = useState(false);

  return (
    <>
      <SignedIn>
        <ClerkTokenBridge />
        <OnboardingGate>
          <NavBar />
          <Outlet context={{ setSessionActive } satisfies AppOutletContext} />
          <MobileTabBar hidden={sessionActive} />
        </OnboardingGate>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}
